import { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { notificationsApi } from "store/services/notifications";
import { logout } from "store/slices/authSlice";
import apiRegistry from "utils/apiRegistry";
import { createSocket } from "./createSocket";
import { NOTIFICATION_SOCKET_EVENTS, SOCKET_SERVERS } from "./config";

const getUnreadCount = (payload) => {
  const value = typeof payload === "number"
    ? payload
    : payload?.count
      ?? payload?.unread_count
      ?? payload?.unreadCount
      ?? (typeof payload?.data === "number" ? payload.data : undefined)
      ?? payload?.data?.count
      ?? payload?.data?.unread_count
      ?? payload?.data?.unreadCount;

  const count = Number(value);
  return Number.isFinite(count) ? count : null;
};

/** Keeps notification RTK Query data synchronized with the admin socket. */
const useNotificationSocket = (token) => {
  const dispatch = useDispatch();
  const previousUnreadCount = useRef(null);

  useEffect(() => {
    const socketUrl = SOCKET_SERVERS.admin;
    if (!token || !socketUrl) return undefined;

    const socket = createSocket(socketUrl, token);

    const handleUnreadCount = (payload) => {
      const count = getUnreadCount(payload);
      if (count === null) return;

      dispatch(notificationsApi.util.upsertQueryData(
        "getUnreadNotificationsCount",
        undefined,
        { count },
      ));

      if (previousUnreadCount.current !== null && previousUnreadCount.current !== count) {
        dispatch(notificationsApi.util.invalidateTags(["NotificationsList"]));
      }
      previousUnreadCount.current = count;
    };

    const handleNewNotification = (payload) => {
      // Some server versions include the count with the new notification.
      // Use it immediately when present, otherwise fetch the current count.
      const count = getUnreadCount(payload);
      if (count !== null) {
        handleUnreadCount(payload);
      } else {
        dispatch(notificationsApi.util.invalidateTags(["UnreadNotificationsCount"]));
      }

      dispatch(notificationsApi.util.invalidateTags(["NotificationsList"]));
    };

    const handleAuthExpired = () => {
      apiRegistry.forEach((api) => dispatch(api.util.resetApiState()));
      dispatch(logout());
    };

    const handleConnectError = (error) => {
      // This makes incorrect environment URLs or rejected socket auth visible
      // in the browser console without interrupting Socket.IO reconnection.
      console.error("Notification socket connection failed:", error.message);
    };

    socket.on(NOTIFICATION_SOCKET_EVENTS.unreadCount, handleUnreadCount);
    socket.on(NOTIFICATION_SOCKET_EVENTS.newNotification, handleNewNotification);
    socket.on(NOTIFICATION_SOCKET_EVENTS.authExpired, handleAuthExpired);
    socket.on("connect_error", handleConnectError);
    socket.connect();

    return () => {
      socket.off(NOTIFICATION_SOCKET_EVENTS.unreadCount, handleUnreadCount);
      socket.off(NOTIFICATION_SOCKET_EVENTS.newNotification, handleNewNotification);
      socket.off(NOTIFICATION_SOCKET_EVENTS.authExpired, handleAuthExpired);
      socket.off("connect_error", handleConnectError);
      socket.disconnect();
      previousUnreadCount.current = null;
    };
  }, [dispatch, token]);
};

export default useNotificationSocket;
